import type { MeetingTranscriptSegment } from '@wundo/shared/meetingPage.ts';
import { formatElapsed } from '@/components/practice/practiceFormat.ts';

/**
 * One person speaking, once. A recogniser hands back a segment per breath, so
 * what a reader thinks of as a turn is several of them in a row from the same
 * speaker — this is that, joined back together.
 */
export interface MeetingTranscriptTurn {
  /** Stable within one transcript: the index the turn starts at. */
  key: number;
  /** Null where the recorder could not say who spoke. */
  speaker: string | null;
  /** Seconds into the recording the turn started, where that is known. */
  start: number | null;
  text: string;
}

/**
 * Segments, grouped back into turns.
 *
 * A transcript read as one segment per line is a stutter — "Ja." / "Dus wat ik
 * bedoel" / "is dat we…" — and the name repeated above each of them makes the
 * conversation unreadable. So consecutive segments from the same speaker become
 * one block, timed by the first of them.
 */
export function meetingTranscriptTurns(
  segments: MeetingTranscriptSegment[],
): MeetingTranscriptTurn[] {
  const turns: MeetingTranscriptTurn[] = [];

  segments.forEach((segment, index) => {
    const text = segment.text.trim();
    if (text === '') return;

    const last = turns.at(-1);
    if (last && last.speaker === segment.speaker) {
      last.text = `${last.text} ${text}`;
      return;
    }

    turns.push({
      key: index,
      speaker: segment.speaker,
      start: segment.start,
      text,
    });
  });

  return turns;
}

/**
 * The whole transcript as plain text, one line per turn, for pasting into a
 * ticket: "[03:12] Bea: …". A turn the recorder gave no time for keeps its
 * name and loses the bracket rather than getting a made-up "[00:00]".
 */
export function renderMeetingTranscript(
  turns: MeetingTranscriptTurn[],
  unknownSpeaker: string,
): string {
  return turns
    .map((turn) => {
      const at = turn.start === null ? '' : `[${formatElapsed(turn.start)}] `;
      return `${at}${turn.speaker ?? unknownSpeaker}: ${turn.text}`;
    })
    .join('\n');
}
